// src/components/TeacherNewGroupForm.js
import React, { useState } from "react";
import {
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
  Card,
  CardActionArea,
} from "@mui/material";
import { addDoc, collection, updateDoc, doc, getDocs, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { SUBJECTS } from "../constants";
import { useTranslation } from "react-i18next";
import group01 from "../img/groupGallery/group-01.jpeg";
import group02 from "../img/groupGallery/group-02.jpg";
import group03 from "../img/groupGallery/group-03.png";
import group04 from "../img/groupGallery/group-04.jpg";
import group05 from "../img/groupGallery/group-05.png";
import group06 from "../img/groupGallery/group-06.png";
import "../i18n";

/**
 * TeacherNewGroupForm
 * Keep same UI + same logic, BUT:
 * - Remove the "teacher selection" logic (teacher is fixed = current teacher)
 * - Use the passed-in `teacherDocId` as teacherId
 * - Use the passed-in `levels` + `subjects` (already filtered in TeacherGroups page)
 */
const TeacherNewGroupForm = ({ onClose, setGroups, teacherDocId, levels = [], subjects = [] }) => {
  const { t } = useTranslation();
  const GROUP_IMAGE_OPTIONS = [
    { id: "group_01", src: group01, label: t("Classroom style 1") },
    { id: "group_02", src: group02, label: t("Classroom style 2") },
    { id: "group_03", src: group03, label: t("Classroom style 3 ") },
    { id: "group_04", src: group04, label: t("Classroom style 4") },
    { id: "group_05", src: group05, label: t("Classroom style 5") },
    { id: "group_06", src: group06, label: t("Classroom style 6") },
  ].slice(0, 6);
  const [error, setError] = useState("");
  const [disabledButton, setDisabledButton] = useState(false);
  const [groupData, setGroupData] = useState({
    name: "",
    level: "",
    subjectId: "",
    teacherId: teacherDocId || "",
    groupImage: "",
  });

  // Validation
  const validateGroupData = () => {
    if (!groupData.name.trim()) {
      setError(t("Group name is required"));
      return false;
    }
    if (!groupData.level) {
      setError(t("Level is required"));
      return false;
    }
    if (!groupData.subjectId) {
      setError(t("Subject is required"));
      return false;
    }
    if (!teacherDocId) {
      setError(t("Unable to identify the current teacher. Please log in again."));
      return false;
    }
    if (!groupData.groupImage) {
      setError(t("Group image is required"));
      return false;
    }
    return true;
  };

  const handleAddGroup = async () => {
    if (!validateGroupData()) return;
    setDisabledButton(true);

    try {
      // Ensure teacherId is always the current teacher doc id
      const payload = {
        ...groupData,
        teacherId: teacherDocId,
      };

      console.log("Validation réussie. Vérification de l'unicité du nom du groupe...");

      // Duplicate name check (same as original)
      const groupsSnapshot = await getDocs(collection(db, "groups"));
      const groupExists = groupsSnapshot.docs.some(
        (d) => (d.data().name || "").trim().toLowerCase() === payload.name.trim().toLowerCase()
      );

      if (groupExists) {
        setError(t("A group with this name already exists. Please choose another name."));
        setDisabledButton(false);
        return;
      }

      console.log("Nom unique validé. Ajout du groupe en cours...");

      // Find subject details (use passed `subjects` first, fallback to global SUBJECTS)
      const selectedSubject =
        (subjects || []).find((s) => s.id === payload.subjectId) ||
        SUBJECTS.find((s) => s.id === payload.subjectId);

      const newGroup = {
        ...payload,
        subjectName: selectedSubject?.name || "",
        subjectLevel: selectedSubject?.level || "",
        studentIds: [],
      };

      // Add group
      const groupDocRef = await addDoc(collection(db, "groups"), newGroup);
      console.log("Groupe ajouté avec succès :", groupDocRef.id);

      // Update teacher.groupIds
      const teacherDocRef = doc(db, "teachers", teacherDocId);
      const teacherSnapshot = await getDoc(teacherDocRef);

      if (!teacherSnapshot.exists()) {
        throw new Error(t("Teacher not found in Firestore."));
      }

      const existingGroupIds = teacherSnapshot.data().groupIds || [];
      const updatedGroupIds = [...new Set([...existingGroupIds, groupDocRef.id])];

      await updateDoc(teacherDocRef, { groupIds: updatedGroupIds });
      console.log("GroupIds mis à jour avec succès pour l'enseignant :", teacherDocId);

      // Update local state immediately
      setGroups((prev) => [...(prev || []), { id: groupDocRef.id, ...newGroup }]);

      // Reset
      setGroupData({ name: "", level: "", subjectId: "", teacherId: teacherDocId, groupImage: "" });
      setDisabledButton(false);
      onClose();
    } catch (e) {
      console.error("Erreur lors de l'ajout du groupe :", e);
      setError(t("An error occurred while adding the group."));
      setDisabledButton(false);
    }
  };

  return (
    <div>
      <Typography sx={{ color: "red", display: "flex", justifyContent: "center" }}>
        {error}
      </Typography>

      <div>
        {/* Group name */}
        <TextField
          label={t("Group name")}
          fullWidth
          margin="normal"
          value={groupData.name}
          onChange={(e) => setGroupData({ ...groupData, name: e.target.value })}
        />

        {/* Level */}
        <FormControl fullWidth margin="normal">
          <Typography variant="subtitle1">{t("Level")}</Typography>
          <Select
            value={groupData.level}
            onChange={(e) =>
              setGroupData({ ...groupData, level: e.target.value, subjectId: "" })
            }
          >
            <MenuItem value="">{t("Select a level")}</MenuItem>
            {(levels || []).map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Subject */}
        <FormControl fullWidth margin="normal">
          <Typography variant="subtitle1">{t("Subject")}</Typography>
          <Select
            value={groupData.subjectId}
            onChange={(e) => setGroupData({ ...groupData, subjectId: e.target.value })}
          >
            <MenuItem value="">{t("Select a subject")}</MenuItem>
            {(subjects || [])
              .filter((subject) => subject.level === groupData.level)
              .map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {/* Teacher (fixed, shown as disabled field to keep UI “same idea”) */}
        <FormControl fullWidth margin="normal">
          <Typography variant="subtitle1">{t("Teacher")}</Typography>
          <Select value={teacherDocId || ""} disabled>
            <MenuItem value={teacherDocId || ""}>{t("Current teacher")}</MenuItem>
          </Select>
        </FormControl>

        {/* Group image (required gallery) */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t("Group image")} *
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" },
              gap: 1.25,
            }}
          >
            {GROUP_IMAGE_OPTIONS.map((img) => {
              const selected = groupData.groupImage === img.src;
              return (
                <Card
                  key={img.id}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: selected ? "2px solid #12344d" : "1px solid #dbe2ea",
                    overflow: "hidden",
                    boxShadow: selected ? "0 8px 18px rgba(18, 52, 77, 0.16)" : "none",
                  }}
                >
                  <CardActionArea
                    onClick={() => setGroupData({ ...groupData, groupImage: img.src })}
                    sx={{ position: "relative" }}
                  >
                    <Box
                      component="img"
                      src={img.src}
                      alt={img.label}
                      sx={{
                        width: "100%",
                        height: 96,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <Box
                      sx={{
                        px: 1,
                        py: 0.65,
                        bgcolor: selected ? "rgba(18,52,77,0.08)" : "#ffffff",
                        borderTop: "1px solid #e5eaf1",
                      }}
                    >
                      <Typography sx={{ fontSize: 12.5, fontWeight: selected ? 700 : 600, textAlign: "center" }}>
                        {img.label}
                      </Typography>
                    </Box>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        </Box>

        {/* Submit */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleAddGroup}
          sx={{ marginTop: "20px", fontSize: "15px" }}
          disabled={disabledButton}
        >
          {t("Add group")}
        </Button>
      </div>
    </div>
  );
};

export default TeacherNewGroupForm;
